import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useGetDispatchCenterCarrierDropoffs,
  useUpdateDispatchCenterCarrierDropoff,
} from "@/api/dispatch-center-dropoffs";
import { PickupHandoffDiagnostics } from "./pickup-handoff-diagnostics";

const DROPOFF_NONE = "__none";

/**
 * Per-(dispatch center, label carrier) default drop-off picker. One row per
 * label carrier the dispatch center's provider is granted to serve. Saves
 * each change immediately via its own endpoint (not the dialog's Save button).
 */
export function DispatchCenterDropoffConfig({
  dispatchCenterId,
  providerCarrierId,
}: {
  dispatchCenterId: string;
  providerCarrierId: string;
}) {
  const {
    data: cells,
    isPending,
    isError,
    isFetching,
    refetch,
  } = useGetDispatchCenterCarrierDropoffs(dispatchCenterId);
  const updateDropoff = useUpdateDispatchCenterCarrierDropoff();

  if (isPending && !isError) {
    return (
      <div role="status" aria-label="Reading handoff setup">
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (isError || !cells) {
    return (
      <div role="alert" className="space-y-2 text-sm">
        <p>Could not read handoff setup. Configuration is unconfirmed.</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          Retry handoff setup
        </Button>
      </div>
    );
  }

  if (cells.length === 0) {
    return (
      <div className="space-y-2 text-xs text-muted-foreground">
        <p>
          This dispatch center's provider isn't granted to serve any label
          carrier yet.
        </p>
        <a href="/add-on-services/grants" className="block underline">
          Platform Grants
        </a>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          Refresh mappings
        </Button>
      </div>
    );
  }

  const handleChange = (labelCarrierId: string, value: string) => {
    const dropoffLocationId = value === DROPOFF_NONE ? null : value;
    updateDropoff.mutate(
      { dispatchCenterId, labelCarrierId, dropoffLocationId },
      {
        onSuccess: () =>
          toast.success(dropoffLocationId ? "Default set" : "Default cleared"),
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error("Failed to update drop-off", {
            description: e.response?.data?.error,
          });
        },
      }
    );
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        A working API quote or label connection, a pickup grant, service
        enablement and a complete receiving depot are separate. Setup alone does
        not guarantee availability for every sender, package, date or route.
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => void refetch()}
        disabled={isFetching}
      >
        Refresh mappings
      </Button>
      {cells.map((cell) => (
        <div key={cell.labelCarrier.id} className="space-y-1">
          <Label className="text-xs">{cell.labelCarrier.name}</Label>
          {cell.dropoffLocations.length === 0 ? (
            <span className="block text-xs text-amber-700">
              ⚠ No active drop-off locations available for{" "}
              {cell.labelCarrier.name}
            </span>
          ) : (
            <Select
              value={cell.defaultDropoffLocationId ?? DROPOFF_NONE}
              onValueChange={(v) => handleChange(cell.labelCarrier.id, v)}
              disabled={updateDropoff.isPending}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="No default — manual assignment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DROPOFF_NONE}>
                  <span className="text-muted-foreground">
                    No default — manual assignment
                  </span>
                </SelectItem>
                {cell.dropoffLocations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.name}
                    {loc.address
                      ? ` · ${loc.address.line1}, ${loc.address.city}`
                      : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <PickupHandoffDiagnostics
            providerCarrierId={providerCarrierId}
            cell={cell}
            refreshingMapping={isFetching}
          />
        </div>
      ))}
    </div>
  );
}
